"use client";
import { useState, Fragment, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { useAuth } from "@/contexts/authContext";
import { ethers } from "ethers";

import { SwiperSlide } from "swiper/react";
import {
  verifyNetwork,
  getUserData,
  initializeContract,
  processClaim,
} from "@/utils/seasonsAirdropClaim";
import { supabase } from "@/supabase/client";

const VideoBanner = dynamic(
  () => import("@/components/new/general/VideoBanner"),
  { ssr: false }
);

import { Badge } from "@/components/ui/custom/ui/badge";

import SeasonsTable from "./components/seasons-table";
import CriteriaSection from "./components/criteria-section";
import ProfileAccordion from "./components/profile-accordion";
import ProfileCard from "./components/profile-card";
import MXSValueOverview from "@/components/ui/custom/datatable/server/MXSValueOverview";
import BoostYourScore from "@/components/ui/custom/BoostYourScore";
import EndOfSeasonRewards from "./components/EndOfSeasonRewards";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/custom/ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/custom/ui/accordion";
import { Icon } from "@iconify/react";
import { cn } from "@/lib/utils";

const FAQS = [
  {
    question:
      "Why does the MXS chart show only Gold at 100%? What about my Ruby and Sapphire points?",
    answer:
      "The MXS chart reflects your actual contribution to total MXS — not just participation. If all of your current MXS comes from Gold, it will show 100% Gold. Even if you have Ruby or Sapphire points, they won't appear in the chart unless they contribute at least some MXS value.",
  },
  {
    question:
      "I have Ruby and Sapphire points — why don't they show in the chart?",
    answer:
      "Each tier has a different multiplier used in the MXS formula:\n- Gold: 7,000\n- Ruby & Sapphire: 1,500\nThis means even if you have some points in Ruby or Sapphire, they may be too small to make a meaningful contribution to your MXS. Once your tier points contribute non-zero MXS, they'll appear in the chart.",
  },
  {
    question: "Is the MXS chart broken or missing data?",
    answer:
      "Nope — it's working exactly as intended! It only displays tiers that actively contribute to your MXS value. This ensures a clean, accurate visual of where your MXS is actually coming from.",
  },
  {
    question:
      "Why is there a difference between the Gold shown in my profile and the one in the MXS Value Overview?",
    answer:
      "This is due to how data is updated in different parts of the platform:\n- Navbar Gold (Top of Page): Shows your real-time Gold balance, updated instantly.\n- MXS Overview & Leaderboard: These are updated periodically based on performance snapshots, used for MXS calculations and leaderboard rankings.",
  },
  {
    question:
      "Which number is more accurate — the one in my profile or in the leaderboard?",
    answer:
      "Both are accurate — they just serve different purposes:\n- The navbar/profile Gold is your true, real-time balance.\n- The MXS table and leaderboard Gold are based on the most recent synced data, ensuring fair comparisons across users.",
  },
  {
    question: "When will the numbers match?",
    answer:
      "Leaderboard and MXS contribution values typically update within a few minutes to a few hours, depending on system load and update frequency. No action is needed from your side.",
  },
];

const SeasonsPage = () => {
  const { isAuthenticated, user, walletAddress } = useAuth();
  const userConnected = isAuthenticated && user && walletAddress;

  const [open, setOpen] = useState();
  const [loading, setLoading] = useState(false);
  const [seasonsTab, setSeasonsTab] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [airdropCriterias, setAirdropCriterias] = useState([]);
  const [loadCriteria, setLoadCriteria] = useState(false);
  const [snapshotFrom, setSnapshotFrom] = useState(null);
  const [snapshotTo, setSnapshotTo] = useState(null);
  const [currentUserResult, setCurrentUserResult] = useState([]);

  const activeSeasonData = useMemo(() => {
    const explicitActiveSeason = seasonsTab.find((season) => season.is_active);
    if (explicitActiveSeason) return explicitActiveSeason;

    const now = Date.now();
    return seasonsTab.find((season) => {
      const start = season.start_date ? new Date(season.start_date).getTime() : NaN;
      const end = season.end_date ? new Date(season.end_date).getTime() : NaN;
      return now >= start && now <= end;
    }) || null;
  }, [seasonsTab]);

  const previousSeasonData = useMemo(() => {
    if (!activeSeasonData) return null;
    const activeNumber = Number(
      activeSeasonData.number ?? activeSeasonData.season_number ?? activeSeasonData.id
    );
    return [...seasonsTab]
      .filter((season) => {
        const seasonNumber = Number(
          season.number ?? season.season_number ?? season.id
        );
        return !season.is_active && seasonNumber < activeNumber;
      })
      .sort(
        (a, b) =>
          Number(b.number ?? b.season_number ?? b.id) -
          Number(a.number ?? a.season_number ?? a.id)
      )[0] || null;
  }, [activeSeasonData, seasonsTab]);

  const handleOpen = (value) => setOpen(open === value ? 0 : value);

  // Define selectedSeasonData first using useMemo
  const selectedSeasonData = useMemo(() => {
    const found = seasonsTab.find((season) => season.name === selectedSeason);
    console.log("[selectedSeasonData] Season selection:", {
      selectedSeason,
      found,
      allSeasons: seasonsTab.map((s) => ({ id: s.id, name: s.name })),
    });
    return found;
  }, [seasonsTab, selectedSeason]);

  // Load initial seasons data
  useEffect(() => {
    const initializeSeasons = async () => {
      try {
        setLoading(true);
        const { data: seasons, error } = await supabase
          .from("seasons_duplicate")
          .select("*")
          // .eq("is_active", true)
          .order("id", { ascending: true });

        if (error) {
          throw new Error("Failed to fetch seasons: " + error.message);
        }

        setSeasonsTab(seasons);
        const activeSeason =
          seasons.find((season) => season.is_active) ||
          seasons.find((season) => {
            const now = Date.now();
            const start = season.start_date
              ? new Date(season.start_date).getTime()
              : NaN;
            const end = season.end_date
              ? new Date(season.end_date).getTime()
              : NaN;
            return now >= start && now <= end;
          });
        if (activeSeason) setSelectedSeason(activeSeason.name);
      } catch (error) {
        console.error("Failed to initialize seasons:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeSeasons();
  }, [walletAddress]);

  // Update criteria when selected season changes
  useEffect(() => {
    const updateAirdropCriteria = async () => {
      if (!selectedSeasonData) return;
      // console.log(selectedSeasonData)

      try {
        setLoadCriteria(true);
        // Check if criteria exists and set it directly
        if (selectedSeasonData.seasons_airdrop_criterias) {
          setAirdropCriterias(selectedSeasonData);
          // console.log('Setting airdrop criteria:', selectedSeasonData); // Debug log
        } else {
          // console.log('No airdrop criteria found for season:', selectedSeasonData.name); // Debug log
          setAirdropCriterias([]); // Reset criteria if none found
        }
      } catch (error) {
        console.error("Failed to update airdrop criteria:", error);
        setAirdropCriterias([]); // Reset on error
      } finally {
        setLoadCriteria(false);
      }
    };

    updateAirdropCriteria();
  }, [selectedSeasonData]);

  const [claimButtonLoading, setClaimButtonLoading] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [eligibleUser, setEligibleUser] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        setClaimButtonLoading(true);
        const userData = await getUserData(
          walletAddress,
          selectedSeasonData.id
        );
        if (!userData.signature) {
          setIsEligible(false);
          setEligibleUser(null);
          return;
        }
        setIsEligible(true);
        setEligibleUser(userData);
      } catch (error) {
        console.error("Error checking eligibility:", error);
        setIsEligible(false);
      } finally {
        setClaimButtonLoading(false);
      }
    };

    if (walletAddress && selectedSeasonData) {
      checkEligibility();
    }
  }, [walletAddress, selectedSeasonData]);

  const handleClaim = async () => {
    try {
      setClaimButtonLoading(true);
      await verifyNetwork();
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = await provider.getSigner();
      const contract = await initializeContract(signer);
      const hasClaimed = await contract.claimed(
        walletAddress,
        selectedSeasonData.id
      );
      if (hasClaimed) {
        setStatus("Already claimed for this season!");
        return;
      }

      const result = await processClaim(
        walletAddress,
        eligibleUser,
        selectedSeasonData.id,
        contract
      );
      if (result.success) {
        setStatus("Airdrop claimed successfully! Token added to MetaMask.");
        console.log("Airdrop claimed successfully! Token added to MetaMask.");
        console.clear();
      } else {
        setStatus(result.message || "Transaction failed");
      }
    } catch (err) {
      console.error("Outer error:", err);
      setStatus(err.message || "Transaction failed");
    } finally {
      setClaimButtonLoading(false);
    }
  };

  // console.log(currentUserResult)

  return (
    <Fragment>
      <VideoBanner>
        <SwiperSlide>
          <div className="swiperSlideDiv relative w-full transition-transform duration-[600ms] ease-in-out max-h-48 sm:max-h-64 overflow-hidden">
            {selectedSeasonData && (
              <video
                preload="auto"
                src={selectedSeasonData.banner}
                autoPlay
                loop
                muted
                playsInline
                className="object-cover w-full h-48 sm:h-64"
              />
            )}
          </div>
        </SwiperSlide>
      </VideoBanner>

      <div className="bg-Zinc-950 px-3 sm:px-6">
        <div className="min-h-screen text-white flex flex-col items-center pb-6 sm:pb-10">
          <div className="bg-metaxseed-dark-gray p-3 sm:p-6 rounded-lg shadow-lg text-center w-full max-w-7xl">
            <ProfileAccordion
              user={user}
              walletAddress={walletAddress}
              isEligible={isEligible}
              claimButtonLoading={claimButtonLoading}
              status={status}
              currentUserResult={currentUserResult}
              handleClaim={handleClaim}
              seasonData={selectedSeasonData}
              seasonsTab={seasonsTab}
              selectedSeason={selectedSeason}
              setSelectedSeason={setSelectedSeason}
            />

            <div className="space-y-6 sm:space-y-8">
              <CriteriaSection
                selectedSeasonData={selectedSeasonData}
                airdropCriterias={airdropCriterias}
                currentUserResult={currentUserResult}
                loadCriteria={loadCriteria}
                walletAddress={walletAddress}
                user={user}
                activeSeasonData={activeSeasonData}
                previousSeasonData={previousSeasonData}
              />

              {/* MXS Value Overview Section - Only show when user is logged in */}
              {userConnected && (
                <MXSValueOverview
                  walletAddress={walletAddress}
                  selectedSeasonData={selectedSeasonData}
                />
              )}

              {/* Seasons Table, Boost Your Score, and End of Season Rewards sections - Always show, regardless of login status */}
              {selectedSeasonData && (
                <>
                  <SeasonsTable
                    season={selectedSeasonData}
                    selectedSeasonData={selectedSeasonData}
                    walletAddress={walletAddress}
                    setSnapshotFrom={setSnapshotFrom}
                    setSnapshotTo={setSnapshotTo}
                    setCurrentUserResult={setCurrentUserResult}
                  />
                  <BoostYourScore />
                  {/* End of Season Rewards */}
                  <EndOfSeasonRewards
                    seasonInfo={{
                      number:
                        selectedSeasonData.number ||
                        selectedSeasonData.id ||
                        "",
                      endDate: selectedSeasonData.end_date
                        ? new Date(
                            selectedSeasonData.end_date
                          ).toLocaleDateString()
                        : "",
                      daysLeft: selectedSeasonData.end_date
                        ? Math.max(
                            0,
                            Math.ceil(
                              (new Date(selectedSeasonData.end_date) -
                                new Date()) /
                                (1000 * 60 * 60 * 24)
                            )
                          )
                        : "",
                    }}
                  />
                  {/* FAQ Section */}
                  <Card className="bg-[#1f1f1f] rounded-2xl border border-zinc-700 p-8 w-full mb-4 mt-8">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-white text-2xl font-semibold flex items-center gap-2">
                        <span className="inline-block align-middle">
                          <Icon
                            icon="bi:exclamation-square-fill"
                            className="w-[22px] h-[22px] text-[#a1a1aa]"
                          />
                        </span>
                        Frequently Asked Questions
                      </CardTitle>
                      <p className="text-zinc-400 text-base text-left mt-1">
                        Common questions about the points system
                      </p>
                    </CardHeader>
                    <CardContent className="mt-1">
                      <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((faq, idx) => (
                          <AccordionItem
                            value={`faq-${idx}`}
                            key={idx}
                            className="mb-2 border border-transparent rounded-xl overflow-hidden"
                          >
                            <AccordionTrigger className="text-zinc-200 text-base px-6 py-4 bg-zinc-700 hover:bg-zinc-700 transition-all rounded-xl flex items-center justify-between w-full font-medium">
                              {faq.question}
                            </AccordionTrigger>
                            <AccordionContent className="text-zinc-400 px-6 pb-4 pt-2 text-sm bg-zinc-650">
                              {faq.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default SeasonsPage;
